import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { assetQrPath, toteQrPath, type QrScan } from '@inventohub/shared';
import { QrScanPanel } from '../components/QrScanPanel';
import { lookupErrorMessage, resolveQrScan } from './qr';

interface ScanQrState {
  openScan: () => void;
}

const ScanQrContext = createContext<ScanQrState | null>(null);

export function ScanQrProvider({ children }: { children: ReactNode }): JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);

  const openScan = useCallback(() => {
    setError(null);
    setTyped('');
    setOpen(true);
  }, []);

  const onScan = useCallback(
    (scan: QrScan) => {
      void (async () => {
        setError(null);
        try {
          const found = await resolveQrScan(scan);
          if (!found) {
            setError('No tote or asset matches that QR or typed identity.');
            return;
          }
          setOpen(false);
          if (found.kind === 'tote') {
            await navigate(toteQrPath(found.tote.number, found.tote.qrSlug));
            return;
          }
          await navigate(assetQrPath(found.asset.serial, found.asset.qrSlug));
        } catch (caught: unknown) {
          setError(lookupErrorMessage(caught, 'Could not look up that tote or serial.'));
        }
      })();
    },
    [navigate],
  );

  const value = useMemo(() => ({ openScan }), [openScan]);

  return (
    <ScanQrContext.Provider value={value}>
      {children}
      {open ? (
        <div
          className="overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Scan QR"
          data-testid="scan-qr-overlay"
        >
          <button
            type="button"
            className="overlay__backdrop"
            aria-label="Close scanner"
            onClick={() => setOpen(false)}
          />
          <div className="overlay__sheet">
            <h2>Scan QR</h2>
            {error ? (
              <p className="status status--error" data-testid="home-scan-error">
                {error}
              </p>
            ) : null}
            <QrScanPanel
              cameraOnly
              label="Tote number or serial"
              value={typed}
              onValueChange={setTyped}
              onScan={onScan}
              submitLabel="Open tote or asset"
              inputTestId="scan-qr-typed"
            />
            <button
              type="button"
              className="button button--ghost"
              onClick={() => setOpen(false)}
              data-testid="scan-qr-close"
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </ScanQrContext.Provider>
  );
}

export function useScanQr(): ScanQrState {
  const value = useContext(ScanQrContext);
  if (!value) throw new Error('useScanQr requires ScanQrProvider');
  return value;
}
